import { useCallback, useEffect, useRef } from "react";

export const DEFAULT_AUTOSAVE_DELAY_MS = 1000;

// Plain comparison used to decide whether a field really changed: 5, "5" and 5.0 are the
// same value, and null / undefined / "" are all "empty".
export const sameValue = (a, b) => String(a ?? "") === String(b ?? "");

/**
 * Debounced auto-save, one timer per field.
 *
 * `schedule(name, value)` (call it on every change) waits for `delay` ms without a new change
 * to that field and then calls `save(name, value)` with the latest value. Changing other fields
 * does not delay it.
 *
 * - `flush(name)`   saves a pending field right now (e.g. when the user leaves the field).
 * - `cancel(name)`  drops a pending save (e.g. the user put the old value back).
 * - `isPending(name)`
 * - Anything still pending when the component unmounts is saved, so navigating away within the
 *   delay does not lose the user's last edit.
 *
 * `save` may change between renders; the latest one is always used.
 *
 * @param {(name: string, value: any) => any} save
 * @param {number} [delay]
 */
export const useAutoSave = (save, delay = DEFAULT_AUTOSAVE_DELAY_MS) => {
  const saveRef = useRef(save);
  saveRef.current = save;

  const timers = useRef({});
  const values = useRef({});

  const take = useCallback((name) => {
    if (!(name in timers.current)) return null;
    clearTimeout(timers.current[name]);
    const entry = { value: values.current[name] };
    delete timers.current[name];
    delete values.current[name];
    return entry;
  }, []);

  const schedule = useCallback(
    (name, value) => {
      if (name in timers.current) clearTimeout(timers.current[name]);
      values.current[name] = value;
      timers.current[name] = setTimeout(() => {
        const entry = take(name);
        if (entry) saveRef.current(name, entry.value);
      }, delay);
    },
    [delay, take]
  );

  const flush = useCallback(
    (name) => {
      const entry = take(name);
      return entry ? saveRef.current(name, entry.value) : undefined;
    },
    [take]
  );

  const cancel = useCallback((name) => {
    take(name);
  }, [take]);

  const isPending = useCallback((name) => name in timers.current, []);

  useEffect(
    () => () => {
      Object.keys(timers.current).forEach((name) => {
        const entry = take(name);
        if (entry) saveRef.current(name, entry.value);
      });
    },
    [take]
  );

  return { schedule, flush, cancel, isPending };
};

export default useAutoSave;
